import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Box, Grid, Inline, Text, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import EditorCloseIcon from '@atlaskit/icon/core/migration/close--editor-close';
import Link from '@atlaskit/link';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ADMIN_LIVE_EDIT } from '@confluence/named-routes';

export const BANNER_HEIGHT = 38;

const i18n = defineMessages({
	bannerContent: {
		id: 'live-pages-features.in-product-prompt.global-banner.content',
		description:
			'Banner content to promote live pages early beta and prompt admin users to sign up',
		defaultMessage:
			'Create and edit pages without having to publish. Early <b>Beta</b> access to live editing is now available.',
	},
	bannerCTA: {
		id: 'live-pages-features.in-product-prompt.global-banner.cta',
		description: 'Link to a page that allows user to sign up for live pages beta',
		defaultMessage: 'Try it now',
	},
	bannerCloseLabel: {
		id: 'live-pages-features.in-product-prompt.global-banner.close.label',
		description:
			'Accessibility label for close button on the right side of the banner that allows user to dismiss the banner',
		defaultMessage: 'Close',
	},
});

// TODO: calculate banner height for narrow screens where text has to wrap
const bannerStyles = xcss({
	height: `${BANNER_HEIGHT}px`,
});

const bannerGridStyles = xcss({
	gridTemplateColumns: '1fr auto 1fr',
	height: '100%',
	marginInline: 'space.200',
});

const bannerMessageStyles = xcss({
	gridArea: 'message',
});

const closeButtonStyles = xcss({
	justifySelf: 'end',
	gridArea: 'close',
});

export type LivePagesInProductPromptGlobalBannerProps = {
	onDismiss: () => void;
};

export const LivePagesInProductPromptGlobalBanner = ({
	onDismiss,
}: LivePagesInProductPromptGlobalBannerProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleLinkClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'livePagesInProductPromptGlobalBanner',
				source: 'LivePagesInProductPromptGlobalBanner',
			},
		}).fire();

		onDismiss();
	};

	return (
		<Box backgroundColor="color.background.accent.teal.bolder" xcss={bannerStyles}>
			<Grid templateAreas={['empty message close']} xcss={bannerGridStyles}>
				<Inline
					alignBlock="center"
					alignInline="center"
					grow="fill"
					space="space.100"
					xcss={bannerMessageStyles}
				>
					<Text color="color.text.inverse" testId="live-pages-in-product-prompt-global-banner-text">
						<FormattedMessage
							{...i18n.bannerContent}
							values={{
								b: (chunks) => <b>{chunks}</b>,
							}}
						/>
					</Text>
					{/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
					<Link href={ADMIN_LIVE_EDIT.toUrl()} appearance="inverse" onClick={handleLinkClick}>
						<FormattedMessage {...i18n.bannerCTA} />
					</Link>
				</Inline>
				<Flex alignItems="center" xcss={closeButtonStyles}>
					<IconButton
						icon={(iconProps) => (
							<EditorCloseIcon
								{...iconProps}
								LEGACY_primaryColor={token('color.icon.inverse')}
								spacing="none"
							/>
						)}
						label={<FormattedMessage {...i18n.bannerCloseLabel} />}
						appearance="subtle"
						onClick={onDismiss}
						testId="live-pages-in-product-prompt-global-banner-close"
					/>
				</Flex>
			</Grid>
		</Box>
	);
};
